import { z } from 'zod';

// The tabs a project or brand screen shows, in order, as built by chat-api
// (GET /projects/:projectId/tabs, GET /workspaces/brands/:brandId/tabs). The backend decides which
// tabs the caller may see; the frontend only maps each tab to the screen that renders it.
// A tab's `path` is relative to the project / brand screen's own path.

export const ProjectBuiltinTabKeyEnum = z.enum([
  'patient-journey',
  'segments',
  'widget-library',
  'responses',
  'summary',
  'highlights',
  'tags',
  'entities',
  'files',
  'details',
]);
export type ProjectBuiltinTabKey = z.infer<typeof ProjectBuiltinTabKeyEnum>;

export const BrandBuiltinTabKeyEnum = z.enum(['tracker', 'projects', 'files']);
export type BrandBuiltinTabKey = z.infer<typeof BrandBuiltinTabKeyEnum>;

/** A page attached to the project / brand; its path is `pages/<identifier>`. */
const PageTabSchema = z.object({
  kind: z.literal('page'),
  path: z.string(),
  label: z.string(),
  pageIdentifier: z.uuid(),
  publishedOn: z.coerce.date().nullable(),
});

/** One of the project's quant analyses; its path is `quantanalysis/<id>`. */
const QuantAnalysisTabSchema = z.object({
  kind: z.literal('quant-analysis'),
  path: z.string(),
  label: z.string(),
  quantAnalysisId: z.number().int(),
});

export const ProjectTabSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('builtin'),
    path: z.string(),
    label: z.string(),
    key: ProjectBuiltinTabKeyEnum,
  }),
  PageTabSchema,
  QuantAnalysisTabSchema,
]);
export type ProjectTab = z.infer<typeof ProjectTabSchema>;

export const BrandTabSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('builtin'), path: z.string(), label: z.string(), key: BrandBuiltinTabKeyEnum }),
  PageTabSchema,
]);
export type BrandTab = z.infer<typeof BrandTabSchema>;

export const GetProjectTabsResponseSchema = z.object({ tabs: z.array(ProjectTabSchema) });
export type GetProjectTabsResponse = z.infer<typeof GetProjectTabsResponseSchema>;

export const GetBrandTabsResponseSchema = z.object({ tabs: z.array(BrandTabSchema) });
export type GetBrandTabsResponse = z.infer<typeof GetBrandTabsResponseSchema>;
